/**
 * AudioTextInput Component
 *
 * Componente reutilizable para transcripción de audio a texto.
 *
 * Características:
 * - Botón de micrófono con indicador visual de grabación
 * - Doble modo de interacción:
 *   * Click simple: Toggle on/off para grabar
 *   * Hold-to-record: Mantener pulsado >300ms para grabar mientras se mantiene
 * - Transcripción usando OpenAI Whisper API con chunking
 * - WebSocket para resultados en tiempo real
 * - Callback para enviar texto transcrito al componente padre
 * - Manejo de errores con mensajes descriptivos
 * - Feature flags mediante variables de entorno
 * - Compatible con Windows/Chrome (sin eventos pasivos)
 *
 * Props:
 * - onTranscriptChange: Callback que recibe el texto transcrito
 * - disabled: Deshabilita el botón de grabación
 *
 * Uso:
 * <AudioTextInput
 *   onTranscriptChange={(text) => setMyText(prev => prev + ' ' + text)}
 *   disabled={false}
 * />
 */

import React, { useEffect } from 'react';
import { motion } from 'motion/react';
import useWhisperTranscription from '../../hooks/useWhisperTranscription';

interface AudioTextInputProps {
  onTranscriptChange: (text: string) => void;
  disabled?: boolean;
}

const AudioTextInput: React.FC<AudioTextInputProps> = ({ onTranscriptChange, disabled = false }) => {
  // Verificar si la feature está habilitada
  const isFeatureEnabled = process.env.REACT_APP_ENABLE_AUDIO_TRANSCRIPTION === 'true';

  // Debug: verificar que la variable se está leyendo
  console.log('🎛️ Feature flags:', {
    isFeatureEnabled
  });

  // Usar el hook de Whisper transcription
  const {
    isRecording,
    isStreaming,
    isProcessing,
    transcript,
    partialTranscript,
    error,
    startRecording,
    stopRecording,
    clearTranscript,
    clearError
  } = useWhisperTranscription();

  // isUploading se mapea a isStreaming para compatibilidad
  const isUploading = isStreaming;

  // Estados para manejar click vs hold
  const [pressTimer, setPressTimer] = React.useState<NodeJS.Timeout | null>(null);
  const [isHoldMode, setIsHoldMode] = React.useState(false);
  const [hasPermission, setHasPermission] = React.useState<boolean | null>(null);

  // Enviar transcripción final al componente padre
  useEffect(() => {
    if (transcript) {
      console.log('📝 Enviando transcripción al componente padre:', transcript);
      onTranscriptChange(transcript);
      // Limpiar transcript después de enviarlo para evitar que se envíe múltiples veces
      clearTranscript();
    }
  }, [transcript, onTranscriptChange, clearTranscript]);

  // Limpiar timer al desmontar
  useEffect(() => {
    return () => {
      if (pressTimer) {
        clearTimeout(pressTimer);
      }
    };
  }, [pressTimer]);

  // Verificar permisos de micrófono al montar
  useEffect(() => {
    const checkPermission = async () => {
      // Verificar si navigator.mediaDevices está disponible
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.error('❌ navigator.mediaDevices no está disponible');
        console.error('Asegúrate de que estás usando HTTPS o localhost');
        setHasPermission(false);
        return;
      }

      try {
        const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        setHasPermission(result.state === 'granted');

        // Escuchar cambios en permisos
        result.onchange = () => {
          setHasPermission(result.state === 'granted');
        };
      } catch (err) {
        // Si no se puede verificar, asumir que necesita solicitud
        console.log('No se pudo verificar permisos, se solicitarán al grabar');
        setHasPermission(null);
      }
    };

    checkPermission();
  }, []);

  // Handler para solicitar permisos explícitamente
  const requestPermission = async () => {
    // Verificar si navigator.mediaDevices está disponible
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.error('❌ navigator.mediaDevices no está disponible');
      alert('Tu navegador no soporta grabación de audio, o la página necesita estar en HTTPS. Por favor, asegúrate de acceder al sitio mediante HTTPS o localhost.');
      return;
    }

    try {
      console.log('🎤 Solicitando permisos explícitamente...');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      setHasPermission(true);
      clearError();
      console.log('✅ Permisos concedidos');
    } catch (err) {
      console.error('❌ Error solicitando permisos:', err);
      setHasPermission(false);

      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          alert('Has denegado el permiso para usar el micrófono. Por favor, haz click en el ícono de candado en la barra de direcciones y permite el acceso al micrófono.');
        } else if (err.name === 'NotFoundError') {
          alert('No se encontró ningún micrófono conectado a tu dispositivo.');
        } else {
          alert(`Error: ${err.message}`);
        }
      }
    }
  };

  // No renderizar nada si la feature no está habilitada
  if (!isFeatureEnabled) {
    return null;
  }

  // Patrón hold-to-record: mantener pulsado para grabar
  const handleMouseDown = async (e: React.MouseEvent) => {
    // Permitir detener grabación incluso si isUploading/isProcessing
    if (disabled || (!isRecording && (isUploading || isProcessing))) return;

    // Prevenir que se dispare el onClick cuando estamos en modo hold
    e.preventDefault();

    // Si ya está grabando, no iniciar timer (se detendrá en mouseUp)
    if (isRecording) return;

    // Iniciar timer para detectar hold (>300ms)
    const timer = setTimeout(async () => {
      setIsHoldMode(true);
      if (!isRecording) {
        await startRecording();
      }
    }, 300);

    setPressTimer(timer);
  };

  const handleMouseUp = async (e: React.MouseEvent) => {
    // Permitir detener grabación incluso si isUploading/isProcessing
    if (disabled) return;

    const wasHoldMode = isHoldMode;
    const hadTimer = pressTimer !== null;

    // Cancelar timer si existe
    if (pressTimer) {
      clearTimeout(pressTimer);
      setPressTimer(null);
    }

    // Si estaba en modo hold, detener grabación
    if (wasHoldMode && isRecording) {
      await stopRecording();
      setIsHoldMode(false);
    }
    // Si NO era hold mode (click rápido), hacer toggle
    else if (isRecording) {
      // Detener grabación
      await stopRecording();
    } else if (hadTimer && !wasHoldMode && !isUploading && !isProcessing) {
      // Iniciar grabación solo si no está procesando
      await startRecording();
    }
  };

  const handleTouchStart = async (e: React.TouchEvent) => {
    // NO usar preventDefault() - causa error en Chrome con passive events
    // Permitir detener grabación incluso si isUploading/isProcessing
    if (disabled || (!isRecording && (isUploading || isProcessing))) return;

    // Si ya está grabando, no iniciar timer
    if (isRecording) return;

    // Iniciar timer para detectar hold
    const timer = setTimeout(async () => {
      setIsHoldMode(true);
      if (!isRecording) {
        await startRecording();
      }
    }, 300);

    setPressTimer(timer);
  };

  const handleTouchEnd = async (e: React.TouchEvent) => {
    // NO usar preventDefault()
    if (disabled) return;

    const wasHoldMode = isHoldMode;
    const hadTimer = pressTimer !== null;

    // Cancelar timer si existe
    if (pressTimer) {
      clearTimeout(pressTimer);
      setPressTimer(null);
    }

    // Si estaba en modo hold, detener grabación
    if (wasHoldMode && isRecording) {
      await stopRecording();
      setIsHoldMode(false);
    }
    // Si NO era hold mode (tap rápido), hacer toggle
    else if (isRecording) {
      await stopRecording();
    } else if (hadTimer && !wasHoldMode && !isUploading && !isProcessing) {
      await startRecording();
    }
  };

  return (
    <div className="audio-text-input">
      {/* Mensaje si no hay permisos o no está disponible */}
      {hasPermission === false && (
        <div style={{
          marginBottom: '8px',
          padding: '8px 12px',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '4px',
          fontSize: '0.85rem'
        }}>
          <div style={{ marginBottom: '4px' }}>
            ⚠️ {!navigator.mediaDevices
              ? 'Tu navegador no soporta grabación de audio. Usa HTTPS o localhost.'
              : 'Se necesita permiso para usar el micrófono'}
          </div>
          {navigator.mediaDevices && (
            <button
              onClick={requestPermission}
              style={{
                padding: '4px 12px',
                backgroundColor: '#ffc107',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.85rem'
              }}
            >
              Permitir acceso al micrófono
            </button>
          )}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* Texto explicativo según estado */}
        {!isRecording && !isUploading && !isProcessing && (
          <small style={{
            fontStyle: 'italic',
            color: '#666',
            fontSize: '0.85rem',
            whiteSpace: 'nowrap'
          }}>
            Click o mantén pulsado
          </small>
        )}

        {isRecording && !isHoldMode && (
          <small style={{
            fontStyle: 'italic',
            color: '#dc3545',
            fontSize: '0.85rem',
            whiteSpace: 'nowrap'
          }}>
            Grabando... Click para detener
          </small>
        )}

        {isRecording && isHoldMode && (
          <small style={{
            fontStyle: 'italic',
            color: '#dc3545',
            fontSize: '0.85rem',
            whiteSpace: 'nowrap'
          }}>
            Suelta para enviar...
          </small>
        )}

        {isUploading && (
          <small style={{
            fontStyle: 'italic',
            color: '#0d6efd',
            fontSize: '0.85rem',
            whiteSpace: 'nowrap'
          }}>
            Subiendo audio...
          </small>
        )}

        {isProcessing && (
          <small style={{
            fontStyle: 'italic',
            color: '#0d6efd',
            fontSize: '0.85rem',
            whiteSpace: 'nowrap'
          }}>
            Procesando...
          </small>
        )}

        {/* Botón de micrófono */}
        <motion.button
          type="button"
          className={`btn-microphone ${isRecording ? 'recording' : ''}`}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
          disabled={disabled || (!isRecording && (isUploading || isProcessing))}
          title={
            isUploading ? 'Subiendo audio...' :
            isProcessing ? 'Procesando transcripción...' :
            isRecording ? 'Click para detener grabación' :
            'Click para grabar (o mantén pulsado)'
          }
          aria-label={isRecording ? 'Detener grabación' : 'Iniciar grabación'}
          animate={{
            backgroundColor: isRecording ? '#dc3545' : (isUploading || isProcessing) ? '#0d6efd' : '#000000',
            scale: isRecording ? 1.2 : 1,
            opacity: (isUploading || isProcessing) ? 0.7 : 1
          }}
          transition={{
            duration: 0.2,
            ease: 'easeInOut'
          }}
        >
          {isRecording ? (
            // Icono de stop cuando está grabando
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="6" width="12" height="12" rx="2" />
            </svg>
          ) : (
            // Icono de micrófono
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
              <line x1="12" y1="19" x2="12" y2="23"></line>
              <line x1="8" y1="23" x2="16" y2="23"></line>
            </svg>
          )}

          {/* Indicador de grabación (pulso animado) */}
          {isRecording && <span className="recording-pulse"></span>}
        </motion.button>
      </div>

      {/* Transcripción parcial en tiempo real */}
      {partialTranscript && (
        <div className="partial-transcript">
          <small style={{ fontStyle: 'italic', color: '#666' }}>
            {partialTranscript}
          </small>
        </div>
      )}

      {/* Mensaje de error */}
      {error && (
        <div className="audio-error-message">
          <small className="text-danger">
            <i className="bi bi-exclamation-circle me-1"></i>
            {error}
          </small>
          <button
            type="button"
            className="btn-close-error"
            onClick={clearError}
            aria-label="Cerrar mensaje de error"
          >
            ×
          </button>
        </div>
      )}

      {/* Estilos del componente */}
      <style>{`
        .audio-text-input {
          position: relative;
          display: inline-block;
        }

        .btn-microphone {
          position: relative;
          background-color: #000000;
          border: none;
          color: #ffffff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 12px;
          border-radius: 50%;
          width: 48px;
          height: 48px;
        }

        .btn-microphone:hover:not(:disabled) {
          opacity: 0.85;
        }

        .btn-microphone:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-microphone.recording {
          color: #ffffff;
        }

        .recording-pulse {
          position: absolute;
          width: 56px;
          height: 56px;
          border-radius: 50%;
          border: 2px solid #dc3545;
          animation: pulse-ring 1.5s ease-out infinite;
          pointer-events: none;
        }

        @keyframes pulse-ring {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          100% {
            transform: scale(1.8);
            opacity: 0;
          }
        }

        .partial-transcript {
          position: absolute;
          bottom: 100%;
          right: 0;
          margin-bottom: 8px;
          padding: 8px 12px;
          background-color: #f8f9fa;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          white-space: nowrap;
          max-width: 300px;
          overflow: hidden;
          text-overflow: ellipsis;
          z-index: 10;
        }

        .audio-error-message {
          position: absolute;
          bottom: 100%;
          right: 0;
          margin-bottom: 8px;
          padding: 8px 12px;
          background-color: #f8d7da;
          border: 1px solid #f5c2c7;
          border-radius: 4px;
          display: flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
          z-index: 10;
        }

        .btn-close-error {
          background: none;
          border: none;
          color: #842029;
          font-size: 20px;
          line-height: 1;
          cursor: pointer;
          padding: 0;
          margin-left: 4px;
        }

        .btn-close-error:hover {
          opacity: 0.7;
        }
      `}</style>
    </div>
  );
};

export default AudioTextInput;
